// src/pages/admin/AdminRefundRequests.js
// P1-C (v1.155) : Admin — demandes de remboursement des clients.
// Approbation -> déclenche le moteur de remboursement (P0-C) :
// automatique si carte Payzone, manuel sinon. Refus avec note motivée.
// Maxula

import React, { useEffect, useState, useCallback } from 'react';
import { FiCheck, FiX, FiRotateCcw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { refundAPI } from '../../services/api';

const fmt = (v) => (Number(v) || 0).toFixed(2);
const STATUS = {
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approuvée', cls: 'bg-blue-100 text-blue-700' },
  processed: { label: 'Traitée', cls: 'bg-green-100 text-green-700' },
  rejected: { label: 'Refusée', cls: 'bg-red-100 text-red-700' }
};
const FILTERS = ['pending', 'approved', 'processed', 'rejected', 'all'];
const FILTER_LABELS = { pending: 'En attente', approved: 'Approuvées', processed: 'Traitées', rejected: 'Refusées', all: 'Toutes' };

const AdminRefundRequests = () => {
  const [items, setItems] = useState([]);
  const [reasonLabels, setReasonLabels] = useState({});
  const [pendingCount, setPendingCount] = useState(0);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [decide, setDecide] = useState(null); // { rr, action }
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (st) => {
    try {
      setLoading(true);
      const res = await refundAPI.adminList(st === 'all' ? {} : { status: st });
      setItems(res.data?.data || []);
      setReasonLabels(res.data?.reasonLabels || {});
      setPendingCount(res.data?.pendingCount || 0);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(filter); }, [filter, load]);

  const submitDecision = async () => {
    if (!decide) return;
    setBusy(true);
    try {
      const r = await refundAPI.adminDecide(decide.rr.id, {
        status: decide.action === 'approve' ? 'approved' : 'rejected',
        adminNote: note
      });
      toast.success(r?.data?.message || 'Décision enregistrée');
      setDecide(null); setNote('');
      load(filter);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">Demandes de remboursement</h1>
      <p className="text-gray-500 mb-4">
        Approbation = remboursement automatique (carte Payzone) ou manuel.
        {pendingCount > 0 && <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">{pendingCount} en attente</span>}
      </p>

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm border ${filter === f ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-gray-700 border-gray-300'}`}>
            {FILTER_LABELS[f]}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl shadow overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-gray-400">Chargement...</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-gray-400">Aucune demande {FILTER_LABELS[filter]?.toLowerCase()}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Commande</th>
                <th className="text-left px-4 py-3 font-medium">Client</th>
                <th className="text-right px-4 py-3 font-medium">Montant</th>
                <th className="text-left px-4 py-3 font-medium">Motif</th>
                <th className="text-left px-4 py-3 font-medium">Date</th>
                <th className="text-left px-4 py-3 font-medium">Statut</th>
                <th className="text-right px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((rr) => (
                <tr key={rr.id} className="border-t border-gray-100 align-top">
                  <td className="px-4 py-3 font-mono font-semibold">{rr.order?.orderNumber || `#${rr.orderId}`}
                    <div className="text-xs text-gray-400 font-sans">{rr.order?.paymentMethod || ''}</div>
                  </td>
                  <td className="px-4 py-3">
                    {(rr.user?.firstName || '') + ' ' + (rr.user?.lastName || '')}
                    <div className="text-xs text-gray-400">{rr.user?.email}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold">{fmt(rr.order?.total)} MAD</td>
                  <td className="px-4 py-3">
                    {reasonLabels[rr.reason] || rr.reason}
                    {rr.description && <div className="text-xs text-gray-500 mt-1 max-w-xs whitespace-pre-wrap">{rr.description}</div>}
                    {rr.adminNote && <div className="text-xs text-indigo-600 mt-1 max-w-xs">Note admin : {rr.adminNote}</div>}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{rr.createdAt ? new Date(rr.createdAt).toLocaleDateString('fr-FR') : ''}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS[rr.status]?.cls || 'bg-gray-100 text-gray-600'}`}>
                      {STATUS[rr.status]?.label || rr.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {rr.status === 'pending' ? (
                      <div className="inline-flex gap-2">
                        <button onClick={() => { setDecide({ rr, action: 'approve' }); setNote(''); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-xs font-semibold hover:bg-green-700">
                          <FiCheck size={13} /> Approuver
                        </button>
                        <button onClick={() => { setDecide({ rr, action: 'reject' }); setNote(''); }}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-semibold hover:bg-red-700">
                          <FiX size={13} /> Refuser
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal de décision */}
      {decide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
              {decide.action === 'approve' ? <FiCheck className="text-green-600" /> : <FiRotateCcw className="text-red-600" />}
              {decide.action === 'approve' ? 'Approuver le remboursement' : 'Refuser la demande'}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              Commande <span className="font-mono font-semibold">{decide.rr.order?.orderNumber}</span> — {fmt(decide.rr.order?.total)} MAD
              {decide.action === 'approve' && (
                <span className="block mt-1 text-xs">
                  Carte Payzone : remboursement automatique tenté · Autre méthode : remboursement manuel à effectuer.
                </span>
              )}
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note {decide.action === 'reject' ? '(motif communiqué au client)' : '(optionnelle)'}
            </label>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-primary-200" />
            <div className="flex justify-end gap-3">
              <button type="button" onClick={() => setDecide(null)}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50">
                Annuler
              </button>
              <button type="button" disabled={busy} onClick={submitDecision}
                className={`px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50 ${decide.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`}>
                {busy ? 'Traitement...' : 'Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRefundRequests;
